import { Box, Container, Grid, GridItem, Heading } from '@chakra-ui/react'
import { dehydrate, QueryClient } from '@tanstack/react-query'
import { InformationTabs } from '@stocker/ui-components/custom'
import { Breadcrumbs, Panel, DownloadGrid, Seo } from '@stocker/ui-components/design-system'
import { formatHeadlineColor, CustomHeading, getServerData, buildUrl } from '@stocker/ui-components/helpers'
import type { GetStaticProps, NextPage } from 'next'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useProductCategoryListingQuery } from '@stocker/codegen/pimcore'
import { useAllCollectionsQuery, useCollectionQuery } from '@stocker/codegen/vendure'
import { handlePrefetchError } from '@stocker/codegen/helpers'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface IBreadcrumb {
  __typename?: 'CollectionBreadcrumb' | undefined
  name: string
  id: string
  slug: string
}

export const getStaticPaths = async () => {
  const allCollections = (await useAllCollectionsQuery.fetcher()())
  return {
    paths: allCollections.collections.items.map((collection) => ({
      params: { slug: collection.slug || '404' },
    })),
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const serverData = await getServerData()
  const slug = params?.slug as string

  const qc = new QueryClient()

  const variables = {
    filter: `{"slug": "${slug}"}`,
  }

  try {
    const data = await useProductCategoryListingQuery.fetcher(variables)()
    qc.setQueryData(useProductCategoryListingQuery.getKey(variables), data)
  } catch (error) {
    return handlePrefetchError(error)
  }

  const breadcrumbs = ((await useCollectionQuery.fetcher({ slug })()).collection?.breadcrumbs ?? []).filter(crumb => crumb.name !== '__root_collection__')

  return {
    props: {
      dehydratedState: dehydrate(qc),
      breadcrumbs,
      revalidateTime: Date.now(),
      ...serverData, // Data needed for _app.tsx Header, Footer etc
    },
    revalidate: 600,
  }
}

const Information: NextPage<{ breadcrumbs?: IBreadcrumb[] }> = ({ breadcrumbs }) => {
  const { slug } = useRouter().query
  const intl = useIntl()
  const router = useRouter()

  const { data } = useProductCategoryListingQuery({
    filter: `{"slug": "${slug as string}"}`,
  })

  const systemInformation = useMemo(() => {
    return data?.getProductCategoryListing?.edges?.[0]?.node?.systemInformationItems?.map((block) => {
      return `<strong style='color: var(--chakra-colors-accent-500); display: block;'>${block?.itemHeadline ?? ''}</strong>${block?.itemText ?? ''}<br/>`
    }) ?? []
  }, [data])

  const galleryImages = useMemo(() => {
    if (data?.getProductCategoryListing?.edges?.[0]?.node?.onSiteImages && data.getProductCategoryListing.edges[0]?.node?.onSiteImages.length > 0) {
      return {
        images: data.getProductCategoryListing.edges[0]?.node?.onSiteImages.map((image) => ({
          fullpath: image?.image?.fullpath ?? '',
        })),
      }
    } else {
      return {
        text: intl.formatMessage({ id: 'product-detail--table-images-no-images' }),
      }
    }
  }, [intl, data])

  return (
    <Container maxW="container.page" mb={{ base: 10, lg: 20 }}>
      <Seo title={data?.getProductCategoryListing?.edges?.[0]?.node?.coloredName ?? ''}/>
      <Box my={{ base: 5, lg: 10 }}>
        <Breadcrumbs data={(breadcrumbs?.map(crumb => ({ label: crumb.name, slug: buildUrl('category', crumb.slug) })) ?? [])}/>
      </Box>
      <Grid
        templateAreas={{
          base: '"header" "image" "tabs" "downloads"',
          lg: '"... header ..." "image tabs downloads"',
        }}
        templateColumns={{ base: '1fr', lg: '1fr 2fr 1fr' }}
        gap="5"
      >
        <GridItem
          area="header"
        >
          <Heading
            fontSize="4xl"
            as="h1"
            mt="0"
          >
            {formatHeadlineColor(data?.getProductCategoryListing?.edges?.[0]?.node?.coloredName ?? '')}
          </Heading>
          <CustomHeading
            mt={{ base: 1, lg: 3 }}
            mb="0"
            heading={{
              text: data?.getProductCategoryListing?.edges?.[0]?.node?.subheadline ?? '',
              size: '2xl',
              type: 'h2',
            }}
          />
        </GridItem>
        <GridItem maxW="100%" pos="relative" area="image">
          <Panel width={{ base: '100px', lg: '100%' }}>
            <Image
              src={data?.getProductCategoryListing?.edges?.[0]?.node?.infoImage?.fullpath ?? ''}
              width={data?.getProductCategoryListing?.edges?.[0]?.node?.infoImage?.dimensions?.width ?? 0}
              height={data?.getProductCategoryListing?.edges?.[0]?.node?.infoImage?.dimensions?.height ?? 0}
              sizes="100vw"
              style={{
                width: '100%',
                height: 'auto',
              }}
              alt="Category Image"
            />
          </Panel>
        </GridItem>
        <GridItem area="tabs">
          <Box dangerouslySetInnerHTML={{ __html: data?.getProductCategoryListing?.edges?.[0]?.node?.description ?? '' }} mb={{ base: 4, lg: 10 }}/>
          <InformationTabs tabs={[
            {
              id: 'systeminformation',
              name: intl.formatMessage({ id: 'product-detail--table-system-information' }),
              tabContent: {
                text: systemInformation.join(''),
              },
            },
            {
              id: 'images',
              name: intl.formatMessage({ id: 'product-detail--table-images' }),
              tabContent: galleryImages,
            },
          ]}
          />
        </GridItem>
        <GridItem area="downloads">
          <Box pos="sticky" top="155px" bgColor="gray.50">
            <DownloadGrid
              columns={1}
              heading={{
                text: intl.formatMessage({ id: 'product-detail--downloads' }),
                size: 'xl',
                type: 'h3',
              }}
              downloadItems={data?.getProductCategoryListing?.edges?.[0]?.node?.downloads?.map(download => {
                return {
                  text: download?.metadata?.find((e) => e?.language === router.locale && e?.name === 'nicename')?.data ?? download?.filename ?? '',
                  fileLink: download?.fullpath ?? '',
                  fileName: download?.filename ?? '',
                }
              }) ?? []}
            />
          </Box>
        </GridItem>
      </Grid>
    </Container>
  )
}

export default Information
